import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ViewKanban as KanbanIcon,
  ViewList as ListIcon,
  CalendarMonth as CalendarIcon,
  Assessment as ReportIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  MenuOpen as MenuIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Article as ContentIcon,
  Folder as FolderIcon,
  Analytics as AnalyticsIcon,
  AccessTime as TimesheetIcon,
} from '@mui/icons-material';
import { getUserData, removeUserData, saveUserData } from '../../services/storageService';
import taskService from '../../services/taskService';
import socketService from '../../services/socketService';

import TaskDashboard from './TaskDashboard';
import TaskKanbanBoard from './TaskKanbanBoard';
import TaskListView from './TaskListView';
import TaskCalendarView from './TaskCalendarView';
import TaskDetailModal from './TaskDetailModal';
import TaskFilters from './TaskFilters';
import TaskReportView from './TaskReportView';
import PlanningStorage from './PlanningStorage';
import TikTokReport from './TikTokReport';
import TimesheetManager from './TimesheetManager';
import avatarService from '../../services/avatarService';
import AvatarUpload from '../common/AvatarUpload';

const VIEWS = {
  DASHBOARD: 'dashboard',
  PLANNING: 'planning',
  TIKTOK: 'tiktok',
  TIMESHEET: 'timesheet',
  KANBAN: 'kanban',
  LIST: 'list',
  CALENDAR: 'calendar',
  REPORT: 'report',
};

const TaskManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // State
  const [user, setUser] = useState(null);
  const [activeView, setActiveView] = useState(VIEWS.KANBAN);
  const [tasks, setTasks] = useState({ todo: [], in_progress: [], review: [], done: [] });
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    priority: '',
    status: '',
    assignee: '',
  });

  // UI State
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Profile Modal
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '',
    avatar: user?.avatar || null,
  });
  const [profileLoading, setProfileLoading] = useState(false);

  // New task form
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: 'marketing',
    priority: 'medium',
    assignee: '',
    deadline: '',
  });

  // Check authentication
  useEffect(() => {
    const userData = getUserData();
    if (!userData) {
      navigate('/login', { replace: true });
      return;
    }

    if (userData.role !== 'admin' && 
        (!userData.departmentRoles || !userData.departmentRoles.includes('marketing_dept'))) {
      navigate('/department-selection', { replace: true });
      return;
    }

    setUser(userData);
  }, [navigate]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [kanbanResult, statsResult, usersResult] = await Promise.all([
        taskService.getKanbanTasks(filters),
        taskService.getStats(),
        taskService.getMarketingUsers(),
      ]);

      if (kanbanResult.success) {
        setTasks(kanbanResult.data);
      }
      if (statsResult.success) {
        setStats(statsResult.data);
      }
      if (usersResult.success) {
        setUsers(usersResult.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Lỗi khi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);

  // Socket.io realtime
  useEffect(() => {
    socketService.connect();
    socketService.joinMarketingTasks();

    socketService.onTaskCreated((task) => {
      handleTaskCreated(task);
    });

    socketService.onTaskUpdated((task) => {
      handleTaskUpdatedRealtime(task);
    });

    socketService.onTaskDeleted(({ id }) => {
      handleTaskDeletedRealtime(id);
    });

    return () => {
      socketService.leaveMarketingTasks();
      socketService.removeAllListeners();
    };
  }, []);

  // Realtime handlers
  const handleTaskCreated = (task) => {
    setTasks((prev) => ({
      ...prev,
      [task.status]: [...(prev[task.status] || []), task],
    }));
    showSnackbar('Task mới được tạo', 'info');
  };

  const handleTaskUpdatedRealtime = (updatedTask) => {
    setTasks((prev) => {
      const newTasks = { ...prev };
      
      // Remove from old status
      Object.keys(newTasks).forEach((status) => {
        newTasks[status] = newTasks[status].filter((t) => t._id !== updatedTask._id);
      });

      // Add to new status
      newTasks[updatedTask.status] = [...(newTasks[updatedTask.status] || []), updatedTask];
      
      return newTasks;
    });
  };

  const handleTaskDeletedRealtime = (taskId) => {
    setTasks((prev) => {
      const newTasks = { ...prev };
      Object.keys(newTasks).forEach((status) => {
        newTasks[status] = newTasks[status].filter((t) => t._id !== taskId);
      });
      return newTasks;
    });
  };

  // Handlers
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setDetailModalOpen(true);
  };

  const handleTaskUpdated = (updatedTask) => {
    handleTaskUpdatedRealtime(updatedTask);
    fetchData(); // Refresh stats
  };

  const handleTaskDeleted = (taskId) => {
    handleTaskDeletedRealtime(taskId);
    fetchData(); // Refresh stats
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      showSnackbar('Vui lòng nhập tiêu đề task', 'error');
      return;
    }

    try {
      const result = await taskService.createTask({
        ...newTask,
        deadline: newTask.deadline ? new Date(newTask.deadline).toISOString() : null,
        assignee: newTask.assignee || null,
      });

      if (result.success) {
        showSnackbar('Tạo task thành công', 'success');
        setCreateModalOpen(false);
        setNewTask({
          title: '',
          description: '',
          category: 'marketing',
          priority: 'medium',
          assignee: '',
          deadline: '',
        });
        fetchData();
      }
    } catch (error) {
      console.error('Error creating task:', error);
      showSnackbar('Lỗi khi tạo task', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleLogout = () => {
    removeUserData();
    navigate('/login', { replace: true });
  };

  const handleOpenProfile = () => {
    setAnchorEl(null);
    setProfileData({
      fullName: user?.fullName || '',
      avatar: user?.avatar || null,
    });
    setProfileModalOpen(true);
  };

  const handleUpdateProfile = async () => {
    if (!profileData.fullName.trim()) {
      setSnackbar({ open: true, message: 'Vui lòng nhập họ tên', severity: 'error' });
      return;
    }
    
    setProfileLoading(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com').replace(/\/api$/, '');
      const response = await fetch(`${baseUrl}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.userId}`,
        },
        body: JSON.stringify({ fullName: profileData.fullName }),
      });
      
      const result = await response.json();
      if (result.success) {
        // Update localStorage
        const updatedUser = { ...user, fullName: profileData.fullName, avatar: profileData.avatar };
        saveUserData(updatedUser);
        window.location.reload(); // Reload to update user state
      } else {
        setSnackbar({ open: true, message: result.message || 'Lỗi cập nhật', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Lỗi kết nối server', severity: 'error' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAvatarChange = (newAvatar) => {
    setProfileData(prev => ({ ...prev, avatar: newAvatar }));
    // Update user in localStorage immediately
    const updatedUser = { ...user, avatar: newAvatar };
    saveUserData(updatedUser);
  };

  // Menu items
  const menuItems = [
    { id: VIEWS.DASHBOARD, icon: DashboardIcon, text: 'Dashboard' },
    { id: VIEWS.KANBAN, icon: KanbanIcon, text: 'Kanban Board' },
    { id: VIEWS.LIST, icon: ListIcon, text: 'Danh sách' },
    { id: VIEWS.CALENDAR, icon: CalendarIcon, text: 'Lịch' },
    { id: VIEWS.REPORT, icon: ReportIcon, text: 'Báo cáo' },
    { id: VIEWS.PLANNING, icon: FolderIcon, text: 'Tài liệu' },
    { id: VIEWS.TIKTOK, icon: AnalyticsIcon, text: 'TikTok Report' },
    { id: VIEWS.TIMESHEET, icon: TimesheetIcon, text: 'Chấm công' },
  ];

  const drawer = (
    <Box sx={{ width: 260, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box 
        sx={{ 
          p: 2.5, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: { xs: 'block', md: 'none' },
        }}
      >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
          Task Management
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Marketing Team
        </Typography>
      </Box>
      <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
      <List sx={{ px: 1.5, py: 2, flex: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.id}
            onClick={() => {
              setActiveView(item.id);
              setMobileOpen(false);
            }}
            sx={{
              borderRadius: 2.5,
              mb: 0.75,
              py: 1.25,
              backgroundColor: activeView === item.id 
                ? 'linear-gradient(135deg, rgba(102,126,234,0.15) 0%, rgba(118,75,162,0.1) 100%)'
                : 'transparent',
              bgcolor: activeView === item.id ? 'rgba(102,126,234,0.1)' : 'transparent',
              border: activeView === item.id ? '1px solid rgba(102,126,234,0.2)' : '1px solid transparent',
              '&:hover': { 
                backgroundColor: 'rgba(102,126,234,0.08)',
                transform: 'translateX(4px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon 
                sx={{ 
                  color: activeView === item.id ? '#667eea' : '#64748b',
                  fontSize: 22,
                }} 
              />
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: activeView === item.id ? 600 : 500,
                color: activeView === item.id ? '#667eea' : '#475569',
                fontSize: '0.9rem',
              }}
            />
            {activeView === item.id && (
              <Box
                sx={{
                  width: 4,
                  height: 20,
                  borderRadius: 2,
                  bgcolor: '#667eea',
                  ml: 1,
                }}
              />
            )}
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<ContentIcon />}
          onClick={() => navigate('/marketing-dashboard/content')}
          sx={{ 
            borderRadius: 2.5,
            mb: 1,
            py: 1.25,
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
            '&:hover': { 
              background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
            },
          }}
        >
          Content Social
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/marketing-dashboard')}
          sx={{ 
            borderRadius: 2.5,
            borderColor: '#e2e8f0',
            color: '#64748b',
            py: 1.25,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { 
              borderColor: '#667eea',
              color: '#667eea',
              bgcolor: 'rgba(102,126,234,0.05)',
            },
          }}
        >
          Quay lại Dashboard
        </Button>
      </Box>
    </Box>
  );

  if (!user) return null;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          color: '#1e293b',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.15rem' },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {menuItems.find((m) => m.id === activeView)?.text || 'Task Management'}
          </Typography>

          {/* View Toggle (Desktop) */}
          {!isMobile && activeView !== VIEWS.DASHBOARD && activeView !== VIEWS.REPORT && (
            <ToggleButtonGroup
              value={activeView}
              exclusive
              onChange={(e, v) => v && setActiveView(v)}
              size="small"
              sx={{ 
                mr: 2,
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 0.75,
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px !important',
                  mx: 0.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  color: '#64748b',
                  '&.Mui-selected': {
                    bgcolor: '#667eea',
                    color: 'white',
                    borderColor: '#667eea',
                    '&:hover': { bgcolor: '#5a67d8' },
                  },
                  '&:hover': { bgcolor: '#f1f5f9' },
                },
              }}
            >
              <ToggleButton value={VIEWS.KANBAN}>
                <KanbanIcon sx={{ mr: 0.75, fontSize: 18 }} /> Kanban
              </ToggleButton>
              <ToggleButton value={VIEWS.LIST}>
                <ListIcon sx={{ mr: 0.75, fontSize: 18 }} /> List
              </ToggleButton>
              <ToggleButton value={VIEWS.CALENDAR}>
                <CalendarIcon sx={{ mr: 0.75, fontSize: 18 }} /> Calendar
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
            sx={{
              mr: { xs: 1, sm: 2 },
              bgcolor: '#667eea',
              '&:hover': { 
                bgcolor: '#5a67d8',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
              },
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              px: { xs: 2, sm: 3 },
              transition: 'all 0.2s',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Tạo Task</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Tạo</Box>
          </Button>

          <IconButton 
            onClick={fetchData} 
            sx={{ 
              mr: 1,
              color: '#64748b',
              '&:hover': { 
                color: '#667eea',
                bgcolor: 'rgba(102,126,234,0.1)',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>

          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar 
              src={user.avatar}
              sx={{ 
                width: 36, 
                height: 36, 
                bgcolor: avatarService.getColor(user.fullName),
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              {avatarService.getInitials(user.fullName)}
            </Avatar>
          </IconButton>

          <Menu 
            anchorEl={anchorEl} 
            open={Boolean(anchorEl)} 
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                minWidth: 200,
              }
            }}
          >
            <MenuItem onClick={handleOpenProfile} sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  src={user.avatar}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: avatarService.getColor(user.fullName),
                  }}
                >
                  {avatarService.getInitials(user.fullName)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>{user.fullName}</Typography>
                  <Typography variant="caption" color="text.secondary">Chỉnh sửa hồ sơ</Typography>
                </Box>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#ef4444' }}>
              <LogoutIcon sx={{ mr: 1.5 }} /> Đăng xuất
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box component="nav" sx={{ width: { md: 260 }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              width: 260,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              width: 260,
              boxSizing: 'border-box',
              mt: '64px',
              height: 'calc(100% - 64px)',
              borderRight: '1px solid #e2e8f0',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2.5, md: 3 },
          pt: { xs: 2, sm: 3 },
          mt: '64px',
          width: { md: 'calc(100% - 260px)' },
          minHeight: 'calc(100vh - 64px)',
          bgcolor: '#f8fafc',
        }}
      >
        <Container maxWidth="xl" disableGutters sx={{ px: { xs: 0, sm: 1 } }}>
          {/* Filters (not for Dashboard and Report) */}
          {activeView !== VIEWS.DASHBOARD && activeView !== VIEWS.REPORT && (
            <Box sx={{ mb: 2.5 }}>
              <TaskFilters filters={filters} onChange={setFilters} users={users} />
            </Box>
          )}

          {/* Views */}
          {activeView === VIEWS.DASHBOARD && (
            <TaskDashboard stats={stats} loading={loading} />
          )}

          {activeView === VIEWS.KANBAN && (
            <TaskKanbanBoard
              tasks={tasks}
              loading={loading}
              onTaskClick={handleTaskClick}
              onCreateTask={() => setCreateModalOpen(true)}
              onTaskUpdated={handleTaskUpdated}
              onTaskDeleted={handleTaskDeleted}
            />
          )}

          {activeView === VIEWS.LIST && (
            <TaskListView
              tasks={tasks}
              loading={loading}
              onTaskClick={handleTaskClick}
              onTaskUpdated={handleTaskUpdated}
              onTaskDeleted={handleTaskDeleted}
            />
          )}

          {activeView === VIEWS.CALENDAR && (
            <TaskCalendarView
              tasks={tasks}
              loading={loading}
              onTaskClick={handleTaskClick}
            />
          )}

          {activeView === VIEWS.REPORT && <TaskReportView />}
          
          {activeView === VIEWS.PLANNING && <PlanningStorage />}
          
          {activeView === VIEWS.TIKTOK && <TikTokReport />}
          
          {activeView === VIEWS.TIMESHEET && <TimesheetManager />}
        </Container>
      </Box>

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={detailModalOpen}
        task={selectedTask}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedTask(null);
        }}
        onTaskUpdated={handleTaskUpdated}
        users={users}
      />

      {/* Create Task Modal */}
      <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Tạo Task mới</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Tiêu đề"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Mô tả"
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Phân loại</InputLabel>
              <Select
                value={newTask.category}
                onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
                label="Phân loại"
              >
                <MenuItem value="marketing">Marketing</MenuItem>
                <MenuItem value="social">Social Media</MenuItem>
                <MenuItem value="content">Content</MenuItem>
                <MenuItem value="production">Sản xuất</MenuItem>
                <MenuItem value="report">Báo cáo</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Độ ưu tiên</InputLabel>
              <Select
                value={newTask.priority}
                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                label="Độ ưu tiên"
              >
                <MenuItem value="low">Thấp</MenuItem>
                <MenuItem value="medium">Trung bình</MenuItem>
                <MenuItem value="high">Cao</MenuItem>
                <MenuItem value="urgent">Khẩn cấp</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Người phụ trách</InputLabel>
              <Select
                value={newTask.assignee}
                onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}
                label="Người phụ trách"
              >
                <MenuItem value="">Chưa giao</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    {u.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="datetime-local"
              label="Deadline"
              value={newTask.deadline}
              onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCreateModalOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            onClick={handleCreateTask}
            sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5a67d8' } }}
          >
            Tạo Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Modal */}
      <Dialog 
        open={profileModalOpen} 
        onClose={() => setProfileModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>Thông tin cá nhân</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
            <AvatarUpload
              currentAvatar={profileData.avatar}
              userName={profileData.fullName}
              size={100}
              onAvatarChange={handleAvatarChange}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Click để đổi ảnh đại diện
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label="Họ và tên"
            value={profileData.fullName}
            onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
            sx={{ mt: 2 }}
          />
          
          <TextField
            fullWidth
            label="Tên đăng nhập"
            value={user?.username || ''}
            disabled
            sx={{ mt: 2 }}
            helperText="Không thể thay đổi"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setProfileModalOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            onClick={handleUpdateProfile}
            disabled={profileLoading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)' },
            }}
          >
            {profileLoading ? 'Đang lưu...' : 'Lưu thay đổi'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskManagement;
